// Formatlash yordamchilarining PANEL DARVOZASI (barrel).
//
// ⚠️ SAHIFALAR SHU FAYLDAN IMPORT QILADI (`@/shared/lib/format`) — pastdagi
// `export *` qatorlari tufayli nom ro'yxati o'zgarmadi.
//
// Bu faylda FAQAT shu ilovaga xos qism qoladi — VAQT ZONASI. Bayt/son/pul
// qismi uchala panelda bir xil bo'lgani uchun `frontend/shared/format/` ga
// chiqarilgan va shu papkaga KO'CHIRILADI (#81, `../shared/sync.mjs`).
//
// ⚠️ ZONA QISMI NEGA KO'CHIRILMADI: admin bilan imzolari bir xil emas
// (`dateTimeFormat` argument tartibi, `utcTooltip` ajratgichi,
// `zoneOffsetLabel` ning zaxira qiymati). Birlashtirish — xulqni
// o'zgartirish, ya'ni alohida ish. Bu qism `tests/timezone.mjs` bilan
// qoplangan, ya'ni ajralish JIM emas.
//
// Backend: vaqtlar — ISO-8601 UTC. UI: TENANT vaqt zonasi (brauzerniki
// emas), tooltip'da UTC ham.

import i18n, { currentIntlLocale } from '@/app/i18n'

export * from './format.units'
export * from './format.money'

/**
 * KO'RSATILADIGAN VAQT ZONASI — TENANT zonasi (`GET /api/selfcare/me` →
 * `dayTimezone`), brauzerniki EMAS.
 *
 * NEGA: kun chegarasini backend tenant zonasida hisoblaydi ("bugungi
 * trafik" o'sha zonaning yarim tunidan boshlanadi). Agar UI brauzer
 * zonasida chizsa, boshqa zonadagi abonent bir xil yozuvni boshqa kunda
 * ko'radi va operator paneli bilan farq qiladi — bu to'g'ridan-to'g'ri
 * qo'ng'iroqqa aylanadi.
 *
 * ⚠️ MODUL DARAJASIDAGI QIYMAT REAKTIV EMAS — valyuta bilan bir xil qoida:
 * ILDIZDA (`AuthProvider`) o'rnatiladi, `me` yuklanmaguncha himoyalangan
 * sahifalar chizilmaydi (`RequireAuth`). Aks holda vaqtlar avval brauzer
 * zonasida chiqib, keyin sakrab o'zgaradi.
 *
 * Zaxira — brauzer zonasi: tenant zonasi ma'lum bo'lmaguncha vaqt
 * ko'rsatilmay qolgandan ko'ra taxminiy ko'rsatilgani afzal.
 */
let displayTimeZone: string | null = null

/**
 * Zona `Intl` uchun tanishmi.
 *
 * Bu tekshiruv MAJBURIY: noma'lum zona bilan `Intl.DateTimeFormat` ISTISNO
 * tashlaydi, ya'ni bitta xato satr butun ilovadagi HAR BIR vaqtni
 * o'chirib qo'yadi.
 */
export function isValidTimeZone(zone: string): boolean {
  const trimmed = zone.trim()
  if (!trimmed) return false
  try {
    Intl.DateTimeFormat('en-US', { timeZone: trimmed })
    return true
  } catch {
    return false
  }
}

/** Brauzer (qurilma) zonasi — solishtirish va zaxira uchun */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  } catch {
    return 'UTC'
  }
}

/** Tenant zonasini o'rnatadi. Noto'g'ri qiymat RAD ETILADI — eskisi qoladi. */
export function setDisplayTimeZone(zone: string | null | undefined): void {
  const next = zone?.trim()
  if (next && isValidTimeZone(next)) displayTimeZone = next
}

/** Amaldagi ko'rsatish zonasi: tenant zonasi, u yo'q bo'lsa brauzerniki */
export function getDisplayTimeZone(): string {
  return displayTimeZone ?? browserTimeZone()
}

/**
 * Zona ofseti belgisi: "UTC+04:30".
 *
 * Manba — `Intl` ning `longOffset` qismi ("GMT+04:30"), qo'lda hisoblash
 * emas: yozgi vaqt (DST) va yarim soatli zonalar (Kobul +04:30, Tehron)
 * shu yo'l bilan o'zidan-o'zi to'g'ri chiqadi. Nol ofsetda `Intl` yolg'iz
 * "GMT" beradi — u "UTC+00:00" ga keltiriladi.
 *
 * Xatoda yiqilmaydi: noma'lum zonada shunchaki "UTC" qaytadi, chunki bu
 * matn faqat izoh sifatida ko'rsatiladi.
 */
export function zoneOffsetLabel(zone?: string): string {
  const target = zone ?? getDisplayTimeZone()
  try {
    const name = Intl.DateTimeFormat('en-US', { timeZone: target, timeZoneName: 'longOffset' })
      .formatToParts(new Date())
      .find((part) => part.type === 'timeZoneName')?.value
    if (!name) return 'UTC'
    const offset = name.replace(/^(GMT|UTC)/, '')
    if (!offset) return 'UTC+00:00'
    const match = /^([+-])(\d{1,2})(?::?(\d{2}))?$/.exec(offset)
    if (!match) return 'UTC'
    const sign = match[1] ?? '+'
    const hours = (match[2] ?? '0').padStart(2, '0')
    const minutes = match[3] ?? '00'
    return `UTC${sign}${hours}:${minutes}`
  } catch {
    return 'UTC'
  }
}

const dateTimeCache = new Map<string, Intl.DateTimeFormat>()

/** Formatlagichlar locale VA ZONA bo'yicha keshlanadi — zonasiz kalit noto'g'ri natija beradi */
function dateTimeFormat(locale: string, zone: string, dateOnly: boolean): Intl.DateTimeFormat {
  const key = `${locale}|${zone}|${String(dateOnly)}`
  let fmt = dateTimeCache.get(key)
  if (!fmt) {
    const options: Intl.DateTimeFormatOptions = dateOnly
      ? { year: 'numeric', month: '2-digit', day: '2-digit' }
      : {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }
    try {
      fmt = new Intl.DateTimeFormat(locale, { ...options, timeZone: zone })
    } catch {
      // Ikkinchi himoya qatlami: zona baribir rad etilsa vaqt YO'QOLMASIN
      fmt = new Intl.DateTimeFormat(locale, options)
    }
    dateTimeCache.set(key, fmt)
  }
  return fmt
}

function formatInZone(d: Date, zone: string): string {
  return dateTimeFormat(currentIntlLocale(), zone, false).format(d)
}

/** ISO UTC → tenant zonasidagi vaqt matni (tanlangan tilga mos) */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return formatInZone(d, getDisplayTimeZone())
}

/** ISO UTC → tenant zonasidagi sana matni */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return dateTimeFormat(currentIntlLocale(), getDisplayTimeZone(), true).format(d)
}

/**
 * Tooltip uchun UCH qator: UTC, tenant zonasi va (agar farq qilsa) brauzer
 * zonasi.
 *
 * Brauzer qatori zona bir xil bo'lganda YOZILMAYDI — bir xil vaqtni ikki
 * marta ko'rsatish foydali emas. Farq bo'lganda esa aynan shu qator
 * "nega mendagi soat boshqa" degan savolga javob beradi.
 */
export function utcTooltip(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const utcText = d.toISOString().replace('T', ' ').replace(/\.\d+Z$/, '')
  const zone = getDisplayTimeZone()
  const browser = browserTimeZone()
  const lines = [
    `${i18n.t('timezone.utc')}: ${utcText}`,
    `${i18n.t('timezone.tenant')} (${zone}, ${zoneOffsetLabel(zone)}): ${formatInZone(d, zone)}`,
  ]
  if (browser !== zone) {
    lines.push(
      `${i18n.t('timezone.browser')} (${browser}, ${zoneOffsetLabel(browser)}): ${formatInZone(d, browser)}`,
    )
  }
  return lines.join('\n')
}

/**
 * BRAUZER zonasi belgisi, masalan "UTC+05:00".
 *
 * ⚠️ Tenant zonasi EMAS — nomi shuni bildiradi va shunday qoladi. Tenant
 * zonasi uchun `zoneOffsetLabel()` argumentsiz chaqiriladi.
 */
export function localZoneLabel(): string {
  const offsetMin = -new Date().getTimezoneOffset()
  const sign = offsetMin >= 0 ? '+' : '-'
  const abs = Math.abs(offsetMin)
  const hh = String(Math.floor(abs / 60)).padStart(2, '0')
  const mm = String(abs % 60).padStart(2, '0')
  return `UTC${sign}${hh}:${mm}`
}
